import React, { useEffect, useState } from "react";
import { useLang } from "../lib/LanguageContext";
import { ASSETS, JOIN_URL } from "../lib/content";
import { Languages, ArrowUpRight } from "lucide-react";

export default function Navbar() {
    const { t, lang, otherHref, otherLang, isRTL } = useLang();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Compact label on mobile (ع / EN), fuller label on desktop (العربية / EN).
    // Labels show the TARGET language (i.e. the language you'll switch TO).
    const shortLabel = otherLang === "ar" ? "ع" : "EN";
    const longLabel = otherLang === "ar" ? "العربية" : "EN";

    // Home link — always scrolls to the top of the current language's page.
    // Using a full anchor to the current route ensures consistent scroll-to-top.
    const homeHref = lang === "ar" ? "/ar#top" : "/#top";

    return (
        <header
            data-testid="sau-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#07111F]/85 border-b border-[color:var(--sau-border)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
                <a
                    href={homeHref}
                    className="flex items-center group flex-shrink-0"
                    aria-label={`${t.meta.brandFull} — ${t.nav.home}`}
                    data-testid="sau-nav-brand"
                >
                    <img
                        src={ASSETS.logoTransparent}
                        alt={t.meta.brandFull}
                        className="h-10 w-10 sm:h-11 sm:w-11 object-contain transition-transform duration-500 group-hover:scale-[1.04]"
                        draggable="false"
                    />
                </a>

                <nav className="hidden md:flex items-center gap-8">
                    {t.nav.links.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            className="text-[13px] text-[color:var(--sau-text-2)] hover:text-[color:var(--sau-text)] transition-colors relative group"
                            data-testid={`sau-nav-link-${l.id}`}
                        >
                            {l.label}
                            <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[color:var(--sau-accent)] transition-all duration-300 group-hover:w-full" />
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                    {/*
                       Language switcher is a plain <a>, not a button.
                       This triggers a full browser navigation to the other language route,
                       guaranteeing a clean, fully-structured page load with zero in-place
                       re-rendering risk (the fix for mobile Arabic rendering issues).
                    */}
                    <a
                        href={otherHref}
                        aria-label={`Switch to ${otherLang === "ar" ? "Arabic" : "English"}`}
                        hrefLang={otherLang}
                        rel="alternate"
                        data-testid="sau-lang-switch"
                        className="inline-flex items-center gap-1.5 sm:gap-2 h-9 px-2.5 sm:px-3 rounded-full sau-btn-ghost text-xs tracking-wide"
                    >
                        <Languages className="w-3.5 h-3.5" aria-hidden />
                        <span className="sm:hidden font-semibold">{shortLabel}</span>
                        <span className="hidden sm:inline">{longLabel}</span>
                    </a>
                    <a
                        href={JOIN_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="sau-nav-cta"
                        className="inline-flex items-center justify-center gap-1.5 h-9 px-3 sm:px-4 rounded-full sau-btn-primary text-[12px] sm:text-xs font-semibold tracking-wide whitespace-nowrap"
                    >
                        {t.nav.cta}
                        <ArrowUpRight className={`w-3.5 h-3.5 ${isRTL ? "-scale-x-100" : ""}`} aria-hidden />
                    </a>
                </div>
            </div>
        </header>
    );
}
